import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAppResume } from "@/shared/hooks/useAppResume";
import { formApiService } from "../services/formApiService";
import { fromFormDtoToForm } from "../types/formTypes";

export const formKeys = {
  business: (userId?: string, businessId?: string) => ["forms", userId, businessId] as const,
  list: (userId?: string, businessId?: string) => [...formKeys.business(userId, businessId), "list"] as const,
  detail: (userId?: string, businessId?: string, formId?: string) =>
    [...formKeys.business(userId, businessId), "detail", formId] as const,
  pinned: (userId?: string, businessId?: string, appointmentId?: string, formId?: string) =>
    [...formKeys.business(userId, businessId), "appointment", appointmentId, formId] as const,
};

export function useForms() {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  return useQuery({
    queryKey: formKeys.list(user?.id, businessId),
    queryFn: async ({ signal }) => {
      if (!user || !businessId) throw new Error("Select a business to load forms.");
      return (await formApiService.getAll(businessId, signal)).map(fromFormDtoToForm);
    },
    enabled: !!user && !!businessId,
    // useFormsReturnRefresh refetches these when the user returns to the app.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useForm(formId?: string, appointmentId?: string) {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  return useQuery({
    queryKey: appointmentId
      ? formKeys.pinned(user?.id, businessId, appointmentId, formId)
      : formKeys.detail(user?.id, businessId, formId),
    queryFn: async ({ signal }) => {
      if (!user || !businessId || !formId) throw new Error("Select a form to preview.");
      const dto = appointmentId
        ? await formApiService.getPinned(businessId, appointmentId, formId, signal)
        : await formApiService.getById(businessId, formId, signal);
      return fromFormDtoToForm(dto);
    },
    enabled: !!user && !!businessId && !!formId,
    // useFormsReturnRefresh refetches these when the user returns to the app.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useFormsReturnRefresh() {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  const queryClient = useQueryClient();
  // Forms can be edited outside the app; refresh when the user comes back, joining any in-flight fetch.
  useAppResume(() => {
    void queryClient.invalidateQueries({ queryKey: formKeys.business(user?.id, businessId) }, { cancelRefetch: false });
  }, !!user?.id && !!businessId);
}
